import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import PartnerExportService from '../../../services/PartnerExportService';

const echapperHtml = (valeur) => String(valeur ?? '').replace(/[&<>"']/g, (caractere) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[caractere]));

const echapperCsv = (valeur) => `"${String(valeur ?? '').replace(/"/g, '""')}"`;

const telechargerCsv = (contenu, nomFichier) => {
  const url = URL.createObjectURL(new Blob([`\ufeff${contenu}`], { type: 'text/csv;charset=utf-8' }));
  const lien = document.createElement('a');
  lien.href = url;
  lien.download = `${nomFichier}.csv`;
  document.body.appendChild(lien);
  try {
    lien.click();
  } finally {
    lien.remove();
    URL.revokeObjectURL(url);
  }
};

// Les écrans fournissent les données métier ; ce module ne connaît que les formats de sortie.
export const exporterTableauPartenaires = ({
  format, lignes, entetes, titre, nomFichier, nomFeuille = 'Données',
  resume = '', filtres = [], rapportExcel = null, feuillesSupplementaires = [], pdf = {}, audit = null,
  imprimerAutomatiquement = true,
}) => {
  const colonnes = entetes || Object.keys(lignes[0] || {});
  const valeurs = lignes.map((ligne) => colonnes.map((colonne) => ligne[colonne] ?? ''));

  if (format === 'xlsx') {
    const classeur = XLSX.utils.book_new();
    if (rapportExcel) {
      const rapport = typeof rapportExcel === 'function'
        ? rapportExcel({ colonnes, valeurs }) : rapportExcel;
      const feuilleRapport = XLSX.utils.aoa_to_sheet(rapport);
      feuilleRapport['!cols'] = colonnes.map((colonne) => ({ wch: Math.max(14, colonne.length + 2) }));
      XLSX.utils.book_append_sheet(classeur, feuilleRapport, 'Rapport');
    }
    const feuille = XLSX.utils.aoa_to_sheet([colonnes, ...valeurs]);
    feuille['!cols'] = colonnes.map((colonne) => ({ wch: Math.max(14, colonne.length + 2) }));
    XLSX.utils.book_append_sheet(classeur, feuille, nomFeuille);
    feuillesSupplementaires.forEach(({ nom, lignes: lignesFeuille, entetes: entetesFeuille }) => {
      const colonnesFeuille = entetesFeuille || Object.keys(lignesFeuille[0] || {});
      const valeursFeuille = lignesFeuille.map((ligne) => colonnesFeuille.map((colonne) => ligne[colonne] ?? ''));
      XLSX.utils.book_append_sheet(classeur,
        XLSX.utils.aoa_to_sheet([colonnesFeuille, ...valeursFeuille]), nom);
    });
    XLSX.writeFile(classeur, `${nomFichier}.xlsx`);
  } else if (format === 'csv') {
    telechargerCsv([colonnes, ...valeurs].map((ligne) => ligne.map(echapperCsv).join(';')).join('\n'), nomFichier);
  } else if (format === 'pdf') {
    const documentPdf = new jsPDF({ orientation: 'landscape' });
    documentPdf.setFontSize(16);
    documentPdf.text(titre, 14, 14);
    documentPdf.setFontSize(9);
    if (resume) documentPdf.text(resume, 14, 21);
    let debut = resume ? 26 : 20;
    if (filtres.length) {
      const texte = documentPdf.splitTextToSize(`Filtres : ${filtres.join(' | ')}`, 260);
      documentPdf.text(texte, 14, debut);
      debut += texte.length * 4 + 2;
    }
    documentPdf.autoTable({
      startY: debut,
      head: [pdf.entetes || colonnes],
      body: pdf.lignes || valeurs,
      styles: { fontSize: pdf.taillePolice || 7, overflow: 'linebreak' },
      headStyles: { fillColor: [23, 61, 97], textColor: 255 },
      ...(pdf.options || {}),
    });
    documentPdf.save(`${nomFichier}.pdf`);
  } else if (format === 'print') {
    const fenetre = window.open('', '_blank', 'width=1200,height=800');
    if (!fenetre) throw new Error("Fenêtre d'impression indisponible");
    const filtresHtml = filtres.length
      ? `<p>Filtres : ${filtres.map(echapperHtml).join(' | ')}</p>` : '';
    const enteteHtml = colonnes.map((colonne) => `<th>${echapperHtml(colonne)}</th>`).join('');
    const lignesHtml = valeurs.map((ligne) => `<tr>${ligne.map((valeur) => `<td>${echapperHtml(valeur)}</td>`).join('')}</tr>`).join('');
    const boutonImpression = imprimerAutomatiquement ? ''
      : '<p class="actions"><button type="button" onclick="window.print()">Imprimer</button></p>';
    fenetre.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${echapperHtml(titre)}</title><style>body{font:12px Arial;padding:20px;color:#173d61}table{width:100%;border-collapse:collapse}th{background:#173d61;color:#fff}th,td{padding:6px;border:1px solid #d5e0eb;text-align:left}tr:nth-child(even){background:#f6f9fc}.actions button{padding:8px 16px;background:#173d61;color:#fff;border:0;border-radius:6px;cursor:pointer}@media print{.actions{display:none}}</style></head><body><h1>${echapperHtml(titre)}</h1>${resume ? `<p>${echapperHtml(resume)}</p>` : ''}${filtresHtml}<table><thead><tr>${enteteHtml}</tr></thead><tbody>${lignesHtml}</tbody></table>${boutonImpression}</body></html>`);
    fenetre.document.close();
    fenetre.focus();
    if (imprimerAutomatiquement) fenetre.print();
  } else {
    throw new Error(`Format d'export inconnu : ${format}`);
  }

  if (audit) {
    PartnerExportService.recordCompletedExport({
      ...audit,
      format: audit.format || format,
      rowCount: audit.rowCount ?? lignes.length,
    });
  }
};
